/**
 * Lifts Shopify Inbox above the sticky ATC only while that bar is stuck.
 * Otherwise Chat keeps Shopify’s original bottom position.
 * Claude 25 Oct 2026: on the home page (phones) Chat is also lifted so it no
 * longer covers the hero call to action.
 *
 * Inbox copies --shopify-agent-activator-offset onto nested shadow hosts;
 * adopted sheets restore the theme offset after expand/collapse.
 */
const OFFSET_SHEET = new CSSStyleSheet();

const OFFSET_SHEET_ACTIVE = `
:host {
  --shopify-agent-activator-offset: var(--bayse-shopify-chat-offset) !important;
}
.activator-nudge {
  bottom: calc(var(--shopify-agent-activator-offset) + var(--private-preview-bar-offset, 0px)) !important;
}
`;

const boundHosts = new WeakSet();

const LAUNCHER_GAP = 16;
const HOME_HERO_OFFSET = 104;
const STICKY_BAR_SELECTOR = '.sticky-add-to-cart__bar';
const COMPACT_MEDIA = '(max-width: 749px)';
const STICKY_ACTIVE_ATTR = 'data-sticky-atc-active';

const compactViewport = window.matchMedia(COMPACT_MEDIA);
/** @type {Element | null} */
let stickyBar = null;
/** @type {ResizeObserver | null} */
let stickyBarResizeObserver = null;

/**
 * Applies or clears the shadow-DOM override used by Shopify Inbox.
 * @param {boolean} active
 */
const setOffsetSheet = (active) => {
  OFFSET_SHEET.replaceSync(active ? OFFSET_SHEET_ACTIVE : '');
};

/**
 * Drops the measured offset so Chat returns to its original position.
 */
const clearLauncherOffset = () => {
  document.documentElement.style.removeProperty('--bayse-shopify-chat-offset');
  document.documentElement.removeAttribute(STICKY_ACTIVE_ATTR);
  setOffsetSheet(false);
};

/**
 * True on the home page, where the hero call to action sits at the bottom of the first screen.
 * @returns {boolean}
 */
const isHomePage = () => Boolean(document.querySelector('main[data-template^="index"]'));

/**
 * Observes sticky-bar size changes so the launcher offset follows wrapping options.
 * @param {Element | null} bar
 */
const bindStickyBar = (bar) => {
  if (bar === stickyBar) return;
  stickyBarResizeObserver?.disconnect();
  stickyBarResizeObserver = null;
  stickyBar = bar;
  if (!bar) return;

  stickyBarResizeObserver = new ResizeObserver(syncLauncherOffset);
  stickyBarResizeObserver.observe(bar);
};

/**
 * Sets --bayse-shopify-chat-offset from the stuck bar on phone and tablet.
 * Lifts Chat on the home page on phones. Clears it otherwise.
 */
const syncLauncherOffset = () => {
  const bar = document.querySelector(STICKY_BAR_SELECTOR);
  bindStickyBar(bar);

  if (!compactViewport.matches || !(bar instanceof HTMLElement) || bar.dataset.stuck !== 'true') {
    if (compactViewport.matches && isHomePage()) {
      document.documentElement.style.setProperty('--bayse-shopify-chat-offset', `${HOME_HERO_OFFSET}px`);
      setOffsetSheet(true);
      return;
    }
    clearLauncherOffset();
    return;
  }

  const barBottom = Number.parseFloat(getComputedStyle(bar).bottom) || 0;
  const clearance = Math.ceil(bar.offsetHeight + barBottom + LAUNCHER_GAP);
  document.documentElement.style.setProperty('--bayse-shopify-chat-offset', `${clearance}px`);
  document.documentElement.setAttribute(STICKY_ACTIVE_ATTR, '');
  setOffsetSheet(true);
};

/**
 * Keeps the launcher offset in sync with sticky-bar visibility, size, and viewport.
 */
const watchStickyBar = () => {
  const attributeObserver = new MutationObserver(syncLauncherOffset);
  attributeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-stuck'],
    subtree: true,
  });

  compactViewport.addEventListener('change', syncLauncherOffset);
  window.addEventListener('resize', syncLauncherOffset);
  syncLauncherOffset();
};

/**
 * Adopts the offset stylesheet into an open shadow root.
 * @param {ShadowRoot | null} root
 */
const injectOffsetSheet = (root) => {
  if (!root) return;
  if (root.adoptedStyleSheets.includes(OFFSET_SHEET)) return;
  root.adoptedStyleSheets = [...root.adoptedStyleSheets, OFFSET_SHEET];
};

/**
 * Binds a Shopify chat host (and nested agent runtime) so collapse keeps theme position.
 * INP risk: low — observer only injects a stylesheet and walks a few custom elements.
 * @param {Element} host
 */
const bindChatHost = (host) => {
  if (!(host instanceof HTMLElement) || boundHosts.has(host)) return;
  boundHosts.add(host);

  const sync = () => {
    injectOffsetSheet(host.shadowRoot);
    const nested = host.shadowRoot?.querySelectorAll('shopify-agent-runtime, shopify-chat');
    nested?.forEach((node) => bindChatHost(node));
  };

  sync();

  const observer = new MutationObserver(sync);
  observer.observe(host, { attributes: true, attributeFilter: ['view', 'open', 'style'] });
  if (host.shadowRoot) {
    observer.observe(host.shadowRoot, { childList: true, subtree: true });
  }
};

/**
 * Finds shopify-chat once it is injected and binds positioning.
 */
const watchForChat = () => {
  const existing = document.querySelector('shopify-chat');
  if (existing) {
    bindChatHost(existing);
    return;
  }

  const wait = new MutationObserver(() => {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    bindChatHost(chat);
    wait.disconnect();
  });
  wait.observe(document.documentElement, { childList: true, subtree: true });
};

setOffsetSheet(false);

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      watchForChat();
      watchStickyBar();
    },
    { once: true }
  );
} else {
  watchForChat();
  watchStickyBar();
}
